import {ActivityHotelState} from "../../types-enums/activity-hotel";
import {hasExactDates} from "./journey";
import {getDurationInDaysOrNights, getMinMaxDates} from "./common";
import {DurationProps} from "../../types-enums/activity-common-properties";
import {HotelClass} from "../../api/model/activity-hotel";
import {DateTime} from "luxon";

/**
 * Determines the duration of a hotel stay in nights based on the provided hotel state
 * and whether to use the original hotel stay data.
 *
 * @param {ActivityHotelState} hotel - The state object representing the hotel's activity details.
 * @param {boolean} useOriginalHotelStay - A flag indicating whether to use the original hotel stay duration.
 * @returns {number | null} The duration of the hotel stay in nights, or null if no valid duration is available.
 */
export const getHotelDurationInNightsWithOptionalOriginalHotelStay = (hotel: ActivityHotelState, useOriginalHotelStay: boolean) => {
    let hotelDurationInNights: number | null = null
    let hotelStay: DurationProps
    if (useOriginalHotelStay) {
        const {originalHotelStayDuration} = hotel
        hotelStay = originalHotelStayDuration
    } else {
        const {hotelStayDuration} = hotel
        hotelStay = hotelStayDuration
    }
    if (hasExactDates(hotelStay)) {
        hotelDurationInNights = getDurationInDaysOrNights(hotelStay, true)
    } else {
        const {daysOrNights} = hotelStay
        hotelDurationInNights = daysOrNights
    }
    return hotelDurationInNights
}

/**
 * Calculates the start and end duration of a hotel stay based on provided parameters for a specific hotel
 * and a collection of hotels.
 *
 * @param {ActivityHotelState[]} allHotels - Array of hotel objects representing different hotel bookings.
 * @param {ActivityHotelState} hotel - The specific hotel object for which the duration is being calculated.
 * @returns {{minValue: number|string, maxValue: number|string} | {minValue: DateTime|null, maxValue: DateTime|null}} An object containing the minimum and maximum
 * values for the hotel stay duration or date range. If exact dates are available, the values will be dates;
 * otherwise, they will represent numeric durations.
 */
export const getStartAndEndDurationOfHotelStay = (allHotels: ActivityHotelState[], hotel: ActivityHotelState): {
    minValue: number | string;
    maxValue: number | string;
} | { minValue: DateTime | null; maxValue: DateTime | null; } => {
    const {common: {groupId}} = hotel
    if (groupId) {
        const hotelsWithGroupId = allHotels.filter(hotel => hotel.common.groupId)
        const durations = hotelsWithGroupId.map(hotel => hotel.hotelStayDuration)
        if (durations.every(duration => hasExactDates(duration))) {
            const {minStartDate, maxEndDate} = getMinMaxDates(durations)
            return {minValue: minStartDate, maxValue: maxEndDate}
        } else {
            return {minValue: 1, maxValue: durations.length}
        }
    } else {
        const {hotelStayDuration} = hotel
        if (hasExactDates(hotelStayDuration)) {
            const {minStartDate, maxEndDate} = getMinMaxDates([hotelStayDuration])
            return {minValue: minStartDate, maxValue: maxEndDate}
        } else {
            return {minValue: 1, maxValue: 1}
        }
    }
}

/**
 * Converts a given hotel class to its corresponding numeric value for use in calculations.
 *
 * @param {HotelClass | null} hotelClass - The hotel class to convert, which can be one of the enumerated values in the HotelClass type or null.
 * @returns {number | null} The numeric value corresponding to the given hotel class, or null if the input is null.
 * @throws {TypeError} If the provided hotelClass is not a valid member of the HotelClass enumeration.
 */
export const getHotelClassNumberValueForCalculationRequest = (hotelClass: HotelClass | null): number | null => {
    if (hotelClass === null) return null
    switch (hotelClass) {
        case HotelClass.zero:
            return 0
        case HotelClass.one:
            return 1
        case HotelClass.two:
            return 2
        case HotelClass.three:
            return 3
        case HotelClass.four:
            return 4
        case HotelClass.five:
            return 5
        default:
            hotelClass satisfies never;
    }
    return null
}

export const getHotelClassFromHotelResponse = (hotelClass: string | null) => {
    if (hotelClass === null) return null
    if (hotelClass === "ZERO") return HotelClass.zero
    if (hotelClass === "ONE") return HotelClass.one
    if (hotelClass === "TWO") return HotelClass.two
    if (hotelClass === "TRHEE") return HotelClass.three
    if (hotelClass === "FOUR") return HotelClass.four
    if (hotelClass === "FIVE") return HotelClass.five
    return null
}